import React, { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { TRUE_KEY, YES_KEY } from '@/common/constants';

import { useHealthPlanNameStore } from '../../BasicProperties/HealthPlanName/stores';
import {
  AccountFundingConfigPaylod,
  CATEGORY_SPEND_LIMIT_ORDER,
  CategorySpendLimitPayload,
  HEALTH_PLAN_TYPE_TO_CATEGORY,
} from '../CoverageLevels.constants';
import SpendLimitsInfoBanner from '../SpendLimitsInfoBanner';

import {
  getAccountFundingItems,
  getCategorySpendLimitField,
  getPerPersonSpendLimitField,
} from './coverageLevelsFields.helpers';

const buildCategorySpendLimitFields = (
  config: AccountFundingConfigPaylod,
  categorySpendLimits: CategorySpendLimitPayload[],
  healthPlanTypes: string[] | undefined
): Field[] => {
  const result: Field[] = [];

  CATEGORY_SPEND_LIMIT_ORDER.forEach((hpType) => {
    if (!(healthPlanTypes || []).includes(hpType)) return;

    const mapping = HEALTH_PLAN_TYPE_TO_CATEGORY[hpType];
    if (!mapping) return;

    const limitItem = categorySpendLimits.find(
      (item) => item.categoryType === mapping.categoryType && item.planId === config.planId
    );
    if (!limitItem) return;

    result.push(getCategorySpendLimitField(limitItem, mapping.label));
  });

  return result;
};

/**
 * Builds account funding, per-person spend limit, and category spend limit fields
 * based on current coverage level's accountFundingConfigs and categorySpendLimits.
 */
const useAccountFundingFields = (
  accountFundingConfigs?: AccountFundingConfigPaylod[],
  categorySpendLimits?: CategorySpendLimitPayload[]
) => {
  const perPersonSpendLimit = useHealthPlanNameStore((state) => state.state.perPersonSpendLimit);
  const perCategorySpendLimit = useHealthPlanNameStore((state) => state.state.perCategorySpendLimit);
  const autocreateCdhEnrollment = useHealthPlanNameStore((state) => state.state.autocreateCdhEnrollment);
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const plans = useHealthPlanNameStore((state) => state.state.plans);

  const hasLinkedPlans = useMemo(
    () => plans?.some((plan): plan is string => typeof plan === 'string' && !!plan) ?? false,
    [plans]
  );

  const isAutocreateEnabled = hasLinkedPlans && autocreateCdhEnrollment === TRUE_KEY;
  const isSpendLimitEnabled = hasLinkedPlans && perPersonSpendLimit === YES_KEY;
  const isCategorySpendLimitEnabled = hasLinkedPlans && perCategorySpendLimit === YES_KEY;

  const planFields = useMemo(() => {
    const fields: Field[] = [];

    if (!accountFundingConfigs) return fields;

    accountFundingConfigs.forEach((config) => {
      const showAutoFunding = isAutocreateEnabled;
      const showSpendLimit = isSpendLimitEnabled;
      const showCategorySpendLimit = isCategorySpendLimitEnabled;

      if (!showAutoFunding && !showSpendLimit && !showCategorySpendLimit) return;

      let isFirstInGroup = true;

      if (showAutoFunding) {
        fields.push(getAccountFundingItems(config));
        isFirstInGroup = false;
      }

      const canSetSpendLimits = config.canSetHealthPlanSpendLimits !== false;

      // When the plan already has spend limits configured, show a banner instead of fields
      if (!canSetSpendLimits && (showSpendLimit || showCategorySpendLimit)) {
        const bannerField = {
          name: `spendLimitsInfoBanner_${config.planId}`,
          type: FieldTypes.Node,
          isFieldGrouped: true,
          fill: true,
          value: <SpendLimitsInfoBanner planId={config.planId} />,
        } as Field;

        if (isFirstInGroup) {
          bannerField.fieldGroupTitle = config?.planName;
        }

        fields.push(bannerField);
        return;
      }

      if (showSpendLimit) {
        const field = getPerPersonSpendLimitField(config);
        if (isFirstInGroup) {
          field.fieldGroupTitle = config?.planName;
        }
        fields.push(field);
        isFirstInGroup = false;
      }

      // Add category spend limit fields for this specific plan
      if (showCategorySpendLimit && categorySpendLimits?.length) {
        const categoryFields = buildCategorySpendLimitFields(config, categorySpendLimits, healthPlanTypes);
        categoryFields.forEach((field) => {
          if (isFirstInGroup) {
            field.fieldGroupTitle = config?.planName;
            isFirstInGroup = false;
          }
          fields.push(field);
        });
      }
    });

    return fields;
  }, [
    accountFundingConfigs,
    isAutocreateEnabled,
    isSpendLimitEnabled,
    isCategorySpendLimitEnabled,
    categorySpendLimits,
    healthPlanTypes,
  ]);

  return planFields;
};

export default useAccountFundingFields;
